// vocalPro.js — Professional vocal DSP: duration-preserving pitch-shift (WSOLA),
// choir harmonizer, lead-vocal chain (level stabilization + EQ + compressor),
// and a classic band-vocoder. All 100% local (Web Audio buffers).

// ---------- small DSP helpers ----------
function hann(n) {
  const w = new Float32Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1));
  return w;
}

function resampleTo(input, outLen) {
  const out = new Float32Array(outLen);
  const M = input.length;
  if (M < 2) return out;
  for (let i = 0; i < outLen; i++) {
    const pos = (i * (M - 1)) / (outLen - 1);
    const idx = Math.floor(pos), frac = pos - idx;
    out[i] = idx + 1 < M ? input[idx] * (1 - frac) + input[idx + 1] * frac : input[idx];
  }
  return out;
}

// Fast O(N) overlap-add time-stretch: output length ≈ alpha * input length, pitch
// unchanged. No correlation search (unlike WSOLA) so it stays real-time even on full
// songs — avoids freezing the tab. Slight phasiness, fine for layered harmonies.
function timeStretch(x, alpha) {
  if (Math.abs(alpha - 1) < 0.001) return Float32Array.from(x);
  const frame = 1024, Hs = frame >> 1;              // 50% overlap
  const Ha = Math.max(1, Math.round(Hs / alpha));
  const win = hann(frame);
  const outLen = Math.ceil(x.length * alpha) + frame + Hs;
  const out = new Float32Array(outLen);
  const norm = new Float32Array(outLen);
  let ana = 0, syn = 0;
  while (ana + frame < x.length && syn + frame < outLen) {
    for (let i = 0; i < frame; i++) { out[syn + i] += x[ana + i] * win[i]; norm[syn + i] += win[i]; }
    ana += Ha; syn += Hs;
  }
  for (let i = 0; i < outLen; i++) if (norm[i] > 1e-6) out[i] /= norm[i];
  return out.subarray(0, Math.max(1, Math.round(x.length * alpha)));
}

// Duration-preserving pitch shift by `semitones` (via WSOLA stretch + resample).
function pitchShift(x, semitones) {
  if (Math.abs(semitones) < 0.01) return Float32Array.from(x);
  const r = Math.pow(2, semitones / 12);
  const stretched = timeStretch(x, r);     // longer (or shorter) by factor r
  return resampleTo(stretched, x.length);  // back to original length -> pitch * r
}

// RBJ biquad (one pass). type: highpass|lowpass|bandpass|highshelf|peaking
function biquad(x, sr, type, f0, Q, gainDb = 0) {
  const A = Math.pow(10, gainDb / 40);
  const w0 = (2 * Math.PI * f0) / sr, cos = Math.cos(w0), sin = Math.sin(w0);
  const alpha = sin / (2 * Q);
  let b0, b1, b2, a0, a1, a2;
  if (type === "highpass") { b0 = (1 + cos) / 2; b1 = -(1 + cos); b2 = (1 + cos) / 2; a0 = 1 + alpha; a1 = -2 * cos; a2 = 1 - alpha; }
  else if (type === "lowpass") { b0 = (1 - cos) / 2; b1 = 1 - cos; b2 = (1 - cos) / 2; a0 = 1 + alpha; a1 = -2 * cos; a2 = 1 - alpha; }
  else if (type === "bandpass") { b0 = alpha; b1 = 0; b2 = -alpha; a0 = 1 + alpha; a1 = -2 * cos; a2 = 1 - alpha; }
  else if (type === "highshelf") { const s = 2 * Math.sqrt(A) * alpha; b0 = A * ((A + 1) + (A - 1) * cos + s); b1 = -2 * A * ((A - 1) + (A + 1) * cos); b2 = A * ((A + 1) + (A - 1) * cos - s); a0 = (A + 1) - (A - 1) * cos + s; a1 = 2 * ((A - 1) - (A + 1) * cos); a2 = (A + 1) - (A - 1) * cos - s; }
  else { b0 = 1 + alpha * A; b1 = -2 * cos; b2 = 1 - alpha * A; a0 = 1 + alpha / A; a1 = -2 * cos; a2 = 1 - alpha / A; } // peaking
  const B0 = b0 / a0, B1 = b1 / a0, B2 = b2 / a0, A1 = a1 / a0, A2 = a2 / a0;
  const out = new Float32Array(x.length);
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  for (let i = 0; i < x.length; i++) {
    const xi = x[i];
    const yi = B0 * xi + B1 * x1 + B2 * x2 - A1 * y1 - A2 * y2;
    x2 = x1; x1 = xi; y2 = y1; y1 = yi; out[i] = yi;
  }
  return out;
}

function deEss(input, sr) {
  const rc = 1 / (2 * Math.PI * 5500), dt = 1 / sr, alpha = rc / (rc + dt);
  const high = new Float32Array(input.length), env = new Float32Array(input.length);
  const envA = Math.exp(-1 / (0.003 * sr));
  let prevIn = 0, prevHigh = 0, e = 0;
  for (let i = 0; i < input.length; i++) {
    const h = alpha * (prevHigh + input[i] - prevIn);
    prevHigh = h; prevIn = input[i]; high[i] = h;
    const rect = Math.abs(h);
    e = rect > e ? rect : e * envA + rect * (1 - envA); env[i] = e;
  }
  const thr = 0.06, out = new Float32Array(input.length);
  for (let i = 0; i < input.length; i++) {
    let g = 1; if (env[i] > thr) g = thr / env[i];
    out[i] = (input[i] - high[i]) + high[i] * g;
  }
  return out;
}

// Slow auto-gain (keeps verses/choruses at an even level before the compressor).
function agc(x, sr, targetRms = 0.13, winSec = 0.35) {
  const N = x.length, a = Math.exp(-1 / (winSec * sr));
  const gains = new Float32Array(N);
  let ms = 0;
  for (let i = 0; i < N; i++) {
    ms = a * ms + (1 - a) * x[i] * x[i];
    const rms = Math.sqrt(ms) + 1e-6;
    gains[i] = Math.max(0.3, Math.min(3.5, targetRms / rms));
  }
  const out = new Float32Array(N);
  let sg = gains[0];
  for (let i = 0; i < N; i++) { sg = a * sg + (1 - a) * gains[i]; out[i] = x[i] * sg; }
  return out;
}

// Feed-forward peak compressor (dB domain).
function compress(x, sr, { thrDb = -20, ratio = 3.5, atkMs = 4, relMs = 120 } = {}) {
  const atk = Math.exp(-1 / ((atkMs / 1000) * sr)), rel = Math.exp(-1 / ((relMs / 1000) * sr));
  const out = new Float32Array(x.length);
  let env = 0;
  for (let i = 0; i < x.length; i++) {
    const r = Math.abs(x[i]);
    env = r > env ? atk * env + (1 - atk) * r : rel * env + (1 - rel) * r;
    const envDb = 20 * Math.log10(env + 1e-9);
    let grDb = 0; if (envDb > thrDb) grDb = (envDb - thrDb) * (1 - 1 / ratio);
    out[i] = x[i] * Math.pow(10, -grDb / 20);
  }
  return out;
}

function normalizePeak(x, target = 0.89) {
  let peak = 0;
  for (let i = 0; i < x.length; i++) { const v = Math.abs(x[i]); if (v > peak) peak = v; }
  if (peak < 1e-6) return x;
  const g = target / peak;
  for (let i = 0; i < x.length; i++) x[i] *= g;
  return x;
}

function envFollow(x, sr, attackSec = 0.02) {
  const a = Math.exp(-1 / (attackSec * sr));
  const out = new Float32Array(x.length);
  let e = 0;
  for (let i = 0; i < x.length; i++) { const r = Math.abs(x[i]); e = r > e ? r : a * e + (1 - a) * r; out[i] = e; }
  return out;
}

function mono(buffer) { return Float32Array.from(buffer.getChannelData(0)); }
function toBuffer(ctx, data, sr) { const b = ctx.createBuffer(1, data.length, sr); b.copyToChannel(data, 0); return b; }

// Simple noise gate — removes bleed/hiss between phrases so harmonies stay clean.
function gate(x, sr, thr = 0.015, atkMs = 2, relMs = 90) {
  const atk = Math.exp(-1 / ((atkMs / 1000) * sr)), rel = Math.exp(-1 / ((relMs / 1000) * sr));
  const out = new Float32Array(x.length);
  let env = 0;
  for (let i = 0; i < x.length; i++) {
    const r = Math.abs(x[i]);
    env = r > env ? atk * env + (1 - atk) * r : rel * env + (1 - rel) * r;
    out[i] = x[i] * (env > thr ? 1 : env / thr);
  }
  return out;
}

// ---------- Phase 1: choir harmonizer (rich, multi-voice) ----------
// Builds several different harmonizations (octave down, 3rd, 5th, octave up, 10th,
// and a slightly detuned unison double) to create a full, balanced choir. Each voice
// is processed with HPF + gate + compressor + limiter/normalize and panned wide.
export function generateHarmonies(ctx, track, opts = {}) {
  const buf = track.buffer, sr = buf.sampleRate;
  const x = mono(buf);
  const keyStr = track.analysis?.key || "C Maj";
  const major = /maj/i.test(keyStr);
  const third = major ? 4 : 3;       // diatonic-ish parallel third
  const tenth = third + 12;          // third an octave up (10th) — airy top voice
  const defs = [
    { semi: -12, label: "Octave grave", pan: -0.1, gain: -3 },
    { semi: third, label: major ? "Tierce maj" : "Tierce min", pan: -0.55, gain: -5 },
    { semi: 7, label: "Quinte", pan: 0.5, gain: -5 },
    { semi: 12, label: "Octave aiguë", pan: 0.15, gain: -8 },
    { semi: tenth, label: "Dixième", pan: 0.7, gain: -9 },
    { semi: 0.12, label: "Double unisson", pan: -0.35, gain: -6 }, // micro-detune for width
  ];
  return defs.map((d) => {
    let y = pitchShift(x, d.semi);
    y = biquad(y, sr, "highpass", 110, 0.707);
    y = gate(y, sr, 0.015);
    y = compress(y, sr, { thrDb: -22, ratio: 3, atkMs: 5, relMs: 120 });
    y = biquad(y, sr, "highshelf", 8000, 0.7, 2); // air so the choir blooms on top
    normalizePeak(y, 0.8);                          // limiter / level balance
    return {
      name: `${track.name} · ${d.label}`,
      buffer: toBuffer(ctx, y, sr),
      category: "voice_choir", vocal: true, voiceType: "choir", isHarmony: true,
      pan: d.pan, gain: d.gain, semi: d.semi,
    };
  });
}

// ---------- Phase 2a: lead-vocal chain ----------
// HPF -> de-ess -> AGC (level stabilize) -> compressor -> presence EQ -> normalize.
export function leadVocalChain(ctx, buffer, opts = {}) {
  const sr = buffer.sampleRate;
  let x = mono(buffer);
  x = biquad(x, sr, "highpass", opts.hpf || 90, 0.707);
  x = deEss(x, sr);
  x = agc(x, sr, opts.targetRms || 0.13, 0.35);
  x = compress(x, sr, { thrDb: -20, ratio: 3.5, atkMs: 4, relMs: 110 });
  x = biquad(x, sr, "peaking", 260, 1.0, -2.5);   // tame mud/boxiness
  x = biquad(x, sr, "peaking", 3000, 1.2, 2);      // intelligibility
  x = biquad(x, sr, "highshelf", 7000, 0.7, 3);    // air/presence
  normalizePeak(x, 0.89);
  return toBuffer(ctx, x, sr);
}

// ---------- Phase 2b: vocoder ----------
// Classic band-vocoder: modulator (voice) shapes a carrier (user-chosen track).
export function vocoder(ctx, modBuffer, carrierBuffer, opts = {}) {
  const sr = modBuffer.sampleRate;
  const mod = mono(modBuffer);
  const carSrc = mono(carrierBuffer);
  const N = mod.length;
  const car = new Float32Array(N);
  for (let i = 0; i < N; i++) car[i] = carSrc.length ? carSrc[i % carSrc.length] : 0;
  const bands = opts.bands || 20;
  const fLo = 150, fHi = 7000;
  const out = new Float32Array(N);
  for (let b = 0; b < bands; b++) {
    const fc = fLo * Math.pow(fHi / fLo, b / (bands - 1));
    const Q = 5;
    const mB = biquad(mod, sr, "bandpass", fc, Q);
    const cB = biquad(car, sr, "bandpass", fc, Q);
    const env = envFollow(mB, sr, 0.015);
    for (let i = 0; i < N; i++) out[i] += cB[i] * env[i] * 2.0;
  }
  // blend in the modulator's sibilance so consonants stay intelligible
  const sib = biquad(mod, sr, "highpass", 5000, 0.707);
  for (let i = 0; i < N; i++) out[i] += sib[i] * 0.5;
  normalizePeak(out, 0.89);
  return toBuffer(ctx, out, sr);
}

// ---------- AI Master: bake an audible per-category treatment into the buffer ----------
// Category-aware EQ + compressor + limiter so the result is clearly "treated" (not subtle).
export function bakeMixChain(ctx, buffer, category) {
  const sr = buffer.sampleRate;
  let x = mono(buffer);
  if (category === "bass") {
    x = biquad(x, sr, "highpass", 30, 0.707);
    x = biquad(x, sr, "peaking", 80, 1.0, 2.5);
    x = biquad(x, sr, "lowpass", 6000, 0.707);
    x = compress(x, sr, { thrDb: -18, ratio: 4, atkMs: 12, relMs: 170 });
  } else if (category === "drum") {
    x = biquad(x, sr, "peaking", 90, 1.0, 2.5);
    x = biquad(x, sr, "peaking", 400, 1.0, -2);
    x = biquad(x, sr, "highshelf", 7000, 0.7, 3);
    x = compress(x, sr, { thrDb: -16, ratio: 4, atkMs: 2, relMs: 90 });
  } else {
    x = biquad(x, sr, "highpass", 100, 0.707);
    x = biquad(x, sr, "peaking", 300, 1.0, -2.5);  // de-mud
    x = biquad(x, sr, "peaking", 3000, 1.1, 2.5);  // presence
    x = biquad(x, sr, "highshelf", 9000, 0.7, 2);  // air
    x = compress(x, sr, { thrDb: -20, ratio: 3, atkMs: 6, relMs: 130 });
  }
  normalizePeak(x, 0.9);
  return toBuffer(ctx, x, sr);
}
